import Head from "next/head";
import { useRouter } from "next/router";
import ErrorPage from "next/error";
import { parseISO, format } from "date-fns";
import { getPost, getAllPostsWithSlug } from "@/lib/posts";
import { Layout } from "@/components/Layout";
import PostBody from "@/components/PostBody";
import PostHeader from "@/components/PostHeader";
import PostTitle from "@/components/PostTitle";
import { SectionContainer } from "@/components/SectionContainer";
import { stripHtml } from "string-strip-html";

export const getStaticProps = async ({ params }) => {
  const data = await getPost(params?.slug);

  return {
    props: {
      post: data.post,
    },
    revalidate: 60,
  };
};

export const getStaticPaths = async () => {
  const allPosts = await getAllPostsWithSlug(true);

  return {
    paths:
      allPosts.edges.map(({ node }) => {
        const date = parseISO(node.date);
        const year = format(date, "yyyy");
        const month = format(date, "M");
        const day = format(date, "d");
        return `/news-archive/${year}/${month}/${day}/${node.slug}`;
      }) || [],
    fallback: false,
  };
};

export default function Post({ post }) {
  const router = useRouter();
  const excerpt = stripHtml(post.excerpt).result;

  if (!router.isFallback && !post?.slug) {
    return <ErrorPage statusCode={404} />;
  }
  return (
    <Layout hideCTA={true}>
      {router.isFallback ? (
        <PostTitle title="Loading...." />
      ) : (
        <>
          <article>
            <Head>
              <title>{`${post.title} - News Archive - Govern For California`}</title>
              <meta name="robots" content="noindex,nofollow" />
              <meta name="description" content={excerpt} />
              <meta name="author" content={post.author.node.name}></meta>
              <meta name="twitter:card" content="summary" />
              <meta name="twitter:site" content="@govern4ca" />
              <meta property="og:title" content={post.title} />
              <meta property="og:description" content={excerpt} />
              {post.featuredImage && (
                <meta
                  property="og:image"
                  content={post.featuredImage?.node.sourceUrl}
                />
              )}
              {!post.featuredImage && (
                <meta
                  property="og:image"
                  content={`https://www.governforcalifornia.org/gfc-og.png`}
                />
              )}
            </Head>
            <SectionContainer bgColor="bg-white" aria-label="Post content">
              <PostHeader
                title={post.title}
                categories={post.categories}
                coverImage={post.featuredImage}
                date={post.date}
                author={post.author}
                prefix="/news-archive"
              />
              <PostBody content={post.content} />
            </SectionContainer>
          </article>
        </>
      )}
    </Layout>
  );
}
